import { Flame, Minus, Plus, Thermometer, Timer } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  formatTemperature,
  formatTotalTime,
  type CookSettings,
  type ParsedTemperature,
} from '../utils/cookSettings';
import type { UnitsPreference } from '../utils/unitsPreference';

const MIN_FACTOR = 0.125;
const MAX_FACTOR = 8;
const clampFactor = (f: number): number => Math.min(MAX_FACTOR, Math.max(MIN_FACTOR, f));

const KIND_LABEL: Record<ParsedTemperature['kind'], string> = {
  oven: 'Oven',
  grill: 'Grill',
  oil: 'Oil',
  internal: 'Done at',
  other: 'Heat to',
};

export interface CookSummaryCardProps {
  settings: CookSettings;
  unitsPreference: UnitsPreference;
  baseServings: number | undefined;
  factor: number;
  onFactorChange: (factor: number) => void;
  /** Shows the servings/scale control — only meaningful when there are ingredients. */
  showScale: boolean;
  /** Tapping a fact jumps to the step it was read from. */
  onJumpToStep: (index: number) => void;
}

/**
 * The top of cook mode: what to set up before you start. The headline is the oven/grill
 * temperature (or, with none, the cooking method) in big type, because it's the thing you
 * glance at from across the kitchen; the smaller facts underneath are the pan, the stovetop
 * heat, other methods, a probe temperature and how much timed cooking the steps add up to.
 * All of it is read from the step text by `parseCookSettings` — so each fact is a button
 * back to the step it came from, where the full sentence says it in context.
 */
export function CookSummaryCard({
  settings,
  unitsPreference,
  baseServings,
  factor,
  onFactorChange,
  showScale,
  onJumpToStep,
}: CookSummaryCardProps): JSX.Element | null {
  const target = unitsPreference === 'metric' ? 'C' : unitsPreference === 'us' ? 'F' : null;
  const headlineTemp =
    settings.temperatures.find((t) => t.kind === 'oven') ??
    settings.temperatures.find((t) => t.kind === 'grill') ??
    settings.temperatures.find((t) => t.kind === 'oil') ??
    null;
  const otherTemps = settings.temperatures.filter((t) => t !== headlineTemp);
  const method = settings.primaryMethod;
  const otherMethods = settings.methods.filter((m) => m.label !== method?.label);

  const hasFacts =
    headlineTemp !== null ||
    method !== null ||
    settings.heat.length > 0 ||
    settings.equipment.length > 0 ||
    settings.timedSeconds > 0;
  if (!hasFacts && !showScale) return null;

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border-2 border-line-strong bg-surface p-4"
      data-testid="cook-summary"
      aria-label="Before you start"
    >
      {(headlineTemp || method) && (
        <div className="flex items-end justify-between gap-3" data-testid="cook-summary-headline">
          {method ? (
            <button
              type="button"
              onClick={() => onJumpToStep(method.stepIndex)}
              className="flex min-w-0 flex-col items-start text-left"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
                Method
              </span>
              <span className="truncate font-display text-[26px] leading-tight tracking-tight text-fg">
                {method.label}
              </span>
            </button>
          ) : (
            <span />
          )}
          {headlineTemp && (
            <button
              type="button"
              data-testid="cook-summary-temp"
              onClick={() => onJumpToStep(headlineTemp.stepIndex)}
              className="flex shrink-0 flex-col items-end text-right"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
                {KIND_LABEL[headlineTemp.kind]}
              </span>
              <span className="font-display text-[34px] leading-none tracking-tight text-accent">
                {formatTemperature(headlineTemp, target)}
              </span>
            </button>
          )}
        </div>
      )}

      {hasFacts && (
        <div className="flex flex-wrap gap-1.5" data-testid="cook-summary-facts">
          {settings.heat.map((h) => (
            <Fact
              key={h.label}
              icon={<Flame size={14} />}
              onClick={() => onJumpToStep(h.stepIndex)}
            >
              {h.label}
            </Fact>
          ))}
          {otherTemps.map((t) => (
            <Fact
              key={`${t.kind}-${t.value}${t.unit}`}
              icon={<Thermometer size={14} />}
              onClick={() => onJumpToStep(t.stepIndex)}
            >
              {KIND_LABEL[t.kind]} {formatTemperature(t, target)}
            </Fact>
          ))}
          {settings.equipment.map((e) => (
            <Fact key={e.label} onClick={() => onJumpToStep(e.stepIndex)}>
              {e.label}
            </Fact>
          ))}
          {otherMethods.map((m) => (
            <Fact key={m.label} muted onClick={() => onJumpToStep(m.stepIndex)}>
              {m.label}
            </Fact>
          ))}
          {settings.timedSeconds > 0 && (
            <span
              className="flex h-9 items-center gap-1.5 rounded-full bg-raised px-3 text-[13px] font-semibold text-fg-2"
              data-testid="cook-summary-timed"
            >
              <Timer size={14} aria-hidden="true" />
              {formatTotalTime(settings.timedSeconds)} of timed steps
            </span>
          )}
        </div>
      )}

      {showScale && (
        <ServingsRow baseServings={baseServings} factor={factor} onFactorChange={onFactorChange} />
      )}
    </section>
  );
}

function Fact({
  children,
  icon,
  muted = false,
  onClick,
}: {
  children: ReactNode;
  icon?: ReactNode;
  muted?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-9 items-center gap-1.5 rounded-full border border-line px-3 text-[13px] font-semibold active:bg-raised ${
        muted ? 'text-fg-3' : 'text-fg'
      }`}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      {children}
    </button>
  );
}

/** The same "Makes N" / ×½ ×2 control as the detail page's `ScaleControls`, cut down to one
 * row. Both write the same per-recipe scale preference, so changing it here is what the
 * detail page shows when you go back. */
function ServingsRow({
  baseServings,
  factor,
  onFactorChange,
}: {
  baseServings: number | undefined;
  factor: number;
  onFactorChange: (factor: number) => void;
}): JSX.Element {
  const scaled = baseServings ? Math.max(1, Math.round(baseServings * factor)) : null;
  const step = (delta: number): void => {
    if (!baseServings) return;
    onFactorChange(clampFactor(Math.max(1, (scaled ?? baseServings) + delta) / baseServings));
  };
  const btn =
    'flex h-11 min-w-11 shrink-0 items-center justify-center rounded-md border-2 border-line-strong px-2 text-[13px] font-semibold text-fg';

  return (
    <div
      className="flex items-center justify-between gap-2 border-t border-line pt-3"
      data-testid="cook-servings"
    >
      {baseServings ? (
        <>
          <span className="text-[13px] font-semibold text-fg-3">Makes</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Fewer servings"
              data-testid="cook-servings-decrease"
              onClick={() => step(-1)}
              className={btn}
            >
              <Minus size={16} aria-hidden="true" />
            </button>
            <span
              data-testid="cook-servings-count"
              className="w-10 text-center text-[18px] font-bold tabular-nums text-fg"
            >
              {scaled}
            </span>
            <button
              type="button"
              aria-label="More servings"
              data-testid="cook-servings-increase"
              onClick={() => step(1)}
              className={btn}
            >
              <Plus size={16} aria-hidden="true" />
            </button>
          </div>
        </>
      ) : (
        <>
          <span className="text-[13px] font-semibold text-fg-3" data-testid="cook-scale-label">
            Scale ×{Math.round(factor * 100) / 100}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onFactorChange(clampFactor(factor * 0.5))}
              className={btn}
            >
              ×½
            </button>
            <button
              type="button"
              onClick={() => onFactorChange(clampFactor(factor * 2))}
              className={btn}
            >
              ×2
            </button>
          </div>
        </>
      )}
    </div>
  );
}
